import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, User, Car, Banknote, Euro, CheckCircle, AlertTriangle, 
  Flag, RefreshCw, ChevronDown, Calendar, Phone, Route, X,
  ArrowUpDown, Filter, Eye, XCircle, RotateCcw
} from 'lucide-react';
import api from '../../services/api';
import { format } from 'date-fns';
import { sq } from 'date-fns/locale/sq';
import { parseUtcDate } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { formatCurrency, formatEuro } from '../../utils/formatters';

const ShiftVerificationPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [shifts, setShifts] = useState([]);
  const [stats, setStats] = useState({
    pending_count: 0,
    closed_count: 0,
    flagged_count: 0,
    today_cash_all: 0,
    today_cash_eur: 0
  });
  const [selectedShift, setSelectedShift] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    received_cash_all: 0,
    received_cash_eur: 0,
    received_at: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch shifts based on active tab
  const fetchShifts = useCallback(async () => {
    try {
      setLoading(true);
      const statusMap = {
        pending: 'pending_verification',
        closed: 'closed',
        flagged: 'flagged'
      };
      
      const response = await api.get('/shifts/all', {
        params: { status: statusMap[activeTab], limit: 100 }
      });
      setShifts(response.data || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error('Gabim gjatë ngarkimit të turneve');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/shifts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchShifts();
    fetchStats();
  }, [fetchShifts, fetchStats]);

  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
      const date = parseUtcDate(dateStr);
      if (!date || isNaN(date.getTime())) return '--';
      return format(date, 'dd MMM yyyy, HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--';
    try {
      const date = parseUtcDate(dateStr);
      if (!date || isNaN(date.getTime())) return '--';
      return format(date, 'HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} orë`;
  };

  // Open verify modal
  const openVerifyModal = (shift) => {
    setSelectedShift(shift);
    setVerifyForm({
      received_cash_all: shift.declared_cash_all || 0,
      received_cash_eur: shift.declared_cash_eur || 0,
      received_at: '',
      notes: ''
    });
    setShowVerifyModal(true);
  };

  // Calculate difference in real-time
  const getDifference = () => {
    if (!selectedShift) return { all: 0, eur: 0 };
    return {
      all: verifyForm.received_cash_all - (selectedShift.declared_cash_all || 0),
      eur: verifyForm.received_cash_eur - (selectedShift.declared_cash_eur || 0)
    };
  };

  // Submit verification
  const handleVerify = async (action) => {
    if (!selectedShift) return;
    
    const diff = getDifference();
    
    // If closing with difference, require notes
    if (action === 'close' && (diff.all !== 0 || diff.eur !== 0)) {
      if (!verifyForm.notes.trim()) {
        toast.error('Kërkohet arsye kur ka diferencë');
        return;
      }
    }
    
    // If flagging, require notes
    if (action === 'flag' && !verifyForm.notes.trim()) {
      toast.error('Arsyeja është e detyrueshme për flagim');
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post(`/shifts/${selectedShift.id}/verify`, {
        action,
        received_cash_all: verifyForm.received_cash_all,
        received_cash_eur: verifyForm.received_cash_eur,
        received_at: verifyForm.received_at || null,
        notes: verifyForm.notes
      });
      
      toast.success(action === 'close' ? 'Turni u mbyll me sukses' : 'Turni u shënua për kontroll');
      setShowVerifyModal(false);
      setSelectedShift(null);
      fetchShifts();
      fetchStats();
    } catch (error) {
      console.error('Error verifying shift:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë verifikimit');
    } finally {
      setSubmitting(false);
    }
  };

  // Reopen shift (admin only)
  const handleReopen = async (shift) => {
    const reason = prompt('Shkruani arsyen e rihapjes:');
    if (!reason || !reason.trim()) {
      toast.error('Arsyeja është e detyrueshme');
      return;
    }
    
    try {
      await api.post(`/shifts/${shift.id}/reopen?reason=${encodeURIComponent(reason)}`);
      toast.success('Turni u rihap me sukses');
      fetchShifts();
      fetchStats();
    } catch (error) {
      console.error('Error reopening shift:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë rihapjes');
    }
  };

  const diff = getDifference();
  const hasDifference = diff.all !== 0 || diff.eur !== 0;

  return (
    <div className="p-6 space-y-6" data-testid="shift-verification-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verifikimi i Turneve</h1>
          <p className="text-sm text-gray-500">Pranimi i cash-it dhe mbyllja finale e turneve</p>
        </div>
        <button
          onClick={() => { fetchShifts(); fetchStats(); }}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
          data-testid="refresh-shifts-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-amber-600 font-medium">Në Pritje</p>
              <p className="text-2xl font-bold text-amber-700" data-testid="pending-count">{stats.pending_count}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-green-600 font-medium">Të Mbyllur</p>
              <p className="text-2xl font-bold text-green-700" data-testid="closed-count">{stats.closed_count}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Flag className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-red-600 font-medium">Të Flaguar</p>
              <p className="text-2xl font-bold text-red-700" data-testid="flagged-count">{stats.flagged_count}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Banknote className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-blue-600 font-medium">Cash Sot (ALL)</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(stats.today_cash_all || 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Euro className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-purple-600 font-medium">Cash Sot (EUR)</p>
              <p className="text-xl font-bold text-purple-700">{formatEuro(stats.today_cash_eur || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {[
            { key: 'pending', label: 'Në Pritje Verifikimi', icon: Clock, count: stats.pending_count, color: 'amber' },
            { key: 'closed', label: 'Të Mbyllur', icon: CheckCircle, count: stats.closed_count, color: 'green' },
            { key: 'flagged', label: 'Të Flaguar', icon: Flag, count: stats.flagged_count, color: 'red' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 transition font-medium ${
                activeTab === tab.key 
                  ? `border-${tab.color}-500 text-${tab.color}-600 bg-${tab.color}-50`
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.key ? `bg-${tab.color}-200 text-${tab.color}-700` : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : shifts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nuk ka turne në këtë kategori</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Shoferi</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Automjeti</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Turni</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Udhëtime</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cash Deklaruar</th>
                  {activeTab !== 'pending' && (
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cash Pranuar</th>
                  )}
                  {activeTab !== 'pending' && (
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Diferenca</th>
                  )}
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Veprime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shifts.map((shift) => (
                  <tr key={shift.id} className="hover:bg-gray-50" data-testid={`shift-row-${shift.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                          {shift.driver_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'SH'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{shift.driver_name}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {shift.driver_phone || '--'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gray-100 rounded font-mono text-sm">
                        {shift.vehicle_plate || '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-600">{formatTime(shift.started_at)} - {formatTime(shift.ended_at)}</p>
                        <p className="text-xs text-gray-400">{formatDuration(shift.duration_minutes)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-blue-600">{shift.rides_count || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>
                        <p className="font-medium">{formatCurrency(shift.declared_cash_all || 0)}</p>
                        {shift.declared_cash_eur > 0 && (
                          <p className="text-xs text-gray-500">{formatEuro(shift.declared_cash_eur)}</p>
                        )}
                      </div>
                    </td>
                    {activeTab !== 'pending' && (
                      <td className="px-4 py-3 text-right">
                        <div>
                          <p className="font-medium">{formatCurrency(shift.received_cash_all || 0)}</p>
                          {shift.received_cash_eur > 0 && (
                            <p className="text-xs text-gray-500">{formatEuro(shift.received_cash_eur)}</p>
                          )}
                        </div>
                      </td>
                    )}
                    {activeTab !== 'pending' && (
                      <td className="px-4 py-3 text-right">
                        <div className={`font-bold ${
                          (shift.difference_all || 0) === 0 ? 'text-green-600' : 
                          (shift.difference_all || 0) > 0 ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {(shift.difference_all || 0) > 0 ? '+' : ''}{formatCurrency(shift.difference_all || 0)}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      {activeTab === 'pending' ? (
                        <button
                          onClick={() => openVerifyModal(shift)}
                          className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition"
                          data-testid={`verify-btn-${shift.id}`}
                        >
                          Verifiko
                        </button>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openVerifyModal(shift)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title="Shiko detajet"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReopen(shift)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Rihap (Admin)"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Verify Modal */}
      {showVerifyModal && selectedShift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="verify-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Verifikimi i Turnit</h2>
                  <p className="text-sm text-gray-300">{selectedShift.driver_name}</p>
                </div>
                <button
                  onClick={() => setShowVerifyModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Shift Summary */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Përmbledhje Turni</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Automjeti:</span>
                    <span className="font-medium">{selectedShift.vehicle_plate || '--'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Kohëzgjatja:</span>
                    <span className="font-medium">{formatDuration(selectedShift.duration_minutes)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Udhëtime:</span>
                    <span className="font-bold text-blue-600">{selectedShift.rides_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Ora:</span>
                    <span className="font-medium">{formatTime(selectedShift.started_at)} - {formatTime(selectedShift.ended_at)}</span>
                  </div>
                </div>
              </div>

              {/* Driver Declared */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="font-semibold text-blue-700 text-sm uppercase tracking-wide mb-3">Cash i Deklaruar nga Shoferi</h3>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-blue-500" />
                    <span className="text-xl font-bold text-blue-700">{formatCurrency(selectedShift.declared_cash_all || 0)}</span>
                  </div>
                  {selectedShift.declared_cash_eur > 0 && (
                    <div className="flex items-center gap-2">
                      <Euro className="w-5 h-5 text-blue-500" />
                      <span className="text-xl font-bold text-blue-700">{formatEuro(selectedShift.declared_cash_eur)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Dispatcher Input - Only for pending */}
              {activeTab === 'pending' || selectedShift.status === 'pending_verification' ? (
                <>
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Cash i Pranuar Fizikisht</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">ALL (Lekë)</label>
                        <div className="relative">
                          <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={verifyForm.received_cash_all}
                            onChange={(e) => setVerifyForm({ ...verifyForm, received_cash_all: parseFloat(e.target.value) || 0 })}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="input-received-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">EUR (Euro)</label>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={verifyForm.received_cash_eur}
                            onChange={(e) => setVerifyForm({ ...verifyForm, received_cash_eur: parseFloat(e.target.value) || 0 })}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            data-testid="input-received-eur"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Optional: Date/Time when cash was received */}
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Data/Ora kur u pranua (opsionale - për rastet e nesërme)
                      </label>
                      <input
                        type="datetime-local"
                        value={verifyForm.received_at}
                        onChange={(e) => setVerifyForm({ ...verifyForm, received_at: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                    </div>
                  </div>

                  {/* Difference Display */}
                  <div className={`rounded-xl p-4 ${
                    !hasDifference ? 'bg-green-50 border border-green-200' :
                    diff.all > 0 || diff.eur > 0 ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <h3 className={`font-semibold text-sm uppercase tracking-wide mb-2 ${
                      !hasDifference ? 'text-green-700' :
                      diff.all > 0 || diff.eur > 0 ? 'text-blue-700' : 'text-red-700'
                    }`}>
                      Diferenca
                    </h3>
                    <div className="flex items-center gap-6">
                      <div className={`text-2xl font-bold ${
                        diff.all === 0 ? 'text-green-600' :
                        diff.all > 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {diff.all > 0 ? '+' : ''}{formatCurrency(diff.all)}
                      </div>
                      {(selectedShift.declared_cash_eur > 0 || verifyForm.received_cash_eur > 0) && (
                        <div className={`text-xl font-bold ${
                          diff.eur === 0 ? 'text-green-600' :
                          diff.eur > 0 ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {diff.eur > 0 ? '+' : ''}{formatEuro(diff.eur)}
                        </div>
                      )}
                    </div>
                    {!hasDifference && (
                      <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Shumat përputhen - mund të mbyllet turni
                      </p>
                    )}
                    {hasDifference && (
                      <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        Ka diferencë - vendosni arsye ose flagoni
                      </p>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Shënim / Arsye {hasDifference && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={verifyForm.notes}
                      onChange={(e) => setVerifyForm({ ...verifyForm, notes: e.target.value })}
                      placeholder="Shkruani arsyen nëse ka diferencë..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      data-testid="input-notes"
                    />
                  </div>
                </>
              ) : (
                /* View mode for closed/flagged */
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide mb-3">Detaje Verifikimi</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Cash i pranuar:</span>
                        <span className="font-medium">{formatCurrency(selectedShift.received_cash_all || 0)} / {formatEuro(selectedShift.received_cash_eur || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Diferenca:</span>
                        <span className={`font-bold ${(selectedShift.difference_all || 0) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(selectedShift.difference_all || 0)} / {formatEuro(selectedShift.difference_eur || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Verifikuar nga:</span>
                        <span className="font-medium">{selectedShift.verifier_name || '--'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Data verifikimit:</span>
                        <span className="font-medium">{formatDateTime(selectedShift.verified_at)}</span>
                      </div>
                      {selectedShift.verification_notes && (
                        <div className="pt-2 border-t border-gray-200">
                          <span className="text-gray-500 block mb-1">Shënim:</span>
                          <p className="text-gray-700">{selectedShift.verification_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            {(activeTab === 'pending' || selectedShift.status === 'pending_verification') && (
              <div className="border-t border-gray-200 p-4 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowVerifyModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
                  disabled={submitting}
                >
                  Anulo
                </button>
                <button
                  onClick={() => handleVerify('flag')}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition flex items-center gap-2"
                  disabled={submitting}
                  data-testid="flag-shift-btn"
                >
                  <Flag className="w-4 h-4" />
                  Shëno për Admin
                </button>
                <button
                  onClick={() => handleVerify('close')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
                  disabled={submitting}
                  data-testid="close-shift-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  {submitting ? 'Duke procesuar...' : 'Mbyll Turnin'}
                </button>
              </div>
            )}
            
            {activeTab !== 'pending' && selectedShift.status !== 'pending_verification' && (
              <div className="border-t border-gray-200 p-4 bg-gray-50 flex items-center justify-end">
                <button
                  onClick={() => setShowVerifyModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                >
                  Mbyll
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftVerificationPage;
